import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { 
  Factory, Trash2, CheckCircle, Filter, Plus, 
  ChevronDown, ChevronUp, Search, Building, User, 
  Phone, Mail, Globe, MapPin, DollarSign, Award, 
  ExternalLink, FileText, Check, X, Eye, Briefcase,
  Pin, ShieldCheck, Layers, Cpu, Laptop
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const IndustryDetailDrawer = ({ industry, onClose, onUpdateStatus }) => {
  if (!industry) return null;

  const companyName = industry.companyLegalName || industry.name || 'Enterprise Employer';
  const tier = industry.partnershipTier || (industry.paymentAmount >= 50000 ? 'Enterprise' : industry.paymentAmount >= 25000 ? 'Growth' : 'Starter');
  const sector = industry.industrySector || industry.sector || 'Technology';

  return (
    <>
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] transition-opacity animate-in fade-in"
        onClick={onClose}
      />
      <div className="fixed inset-y-0 right-0 w-full md:w-[680px] lg:w-[780px] bg-zinc-950 border-l border-zinc-800 shadow-2xl z-[101] flex flex-col animate-in slide-in-from-right duration-300 text-white">
        {/* Header */}
        <div className="p-6 border-b border-zinc-800 bg-zinc-900/60 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="px-3 py-1 bg-teal-500/10 text-teal-400 border border-teal-500/20 text-[10px] font-black uppercase tracking-widest rounded-full">
                Portal 03 &bull; Corporate Talent Partner
              </span>
              <span className="text-xs font-mono font-bold text-amber-400">
                {tier} Tier
              </span>
            </div>
            <h2 className="text-2xl font-black text-white">{companyName}</h2>
            <p className="text-xs text-zinc-400 font-bold mt-1 flex items-center gap-2">
              <Factory size={14} className="text-teal-400" /> {sector} &bull; {industry.county || 'Nairobi'}, {industry.headquartersCountry || industry.country || 'Kenya'}
            </p>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 text-zinc-400 hover:text-white bg-zinc-900 border border-zinc-800 rounded-xl transition-colors cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 text-sm">
          {/* Quick Metrics Bar */}
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 bg-zinc-900/80 border border-zinc-800 rounded-xl">
              <span className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Status</span>
              <span className="text-xs font-bold text-emerald-400 uppercase tracking-wider">
                {industry.status || 'Active'}
              </span>
            </div>
            <div className="p-3 bg-zinc-900/80 border border-zinc-800 rounded-xl">
              <span className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Hiring Quota</span>
              <span className="text-xs font-bold text-white font-mono">
                {industry.annualTechHiringTarget || industry.hiringTarget || 10} Engineers / yr
              </span>
            </div>
            <div className="p-3 bg-zinc-900/80 border border-zinc-800 rounded-xl">
              <span className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Internship Slots</span>
              <span className="text-xs font-bold text-teal-400 font-mono">
                {industry.internshipSlotsPerCohort || 5} per cohort
              </span>
            </div>
          </div>

          {/* Section 1: Entity & Registration */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5 space-y-3">
            <h3 className="text-xs font-black uppercase tracking-widest text-teal-400 border-b border-zinc-800 pb-2 flex items-center gap-2">
              <Building size={14} /> Corporate Entity Profile
            </h3>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="text-zinc-500 font-bold block">Company Legal Name:</span>
                <span className="text-zinc-200 font-bold">{companyName}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Industry Sector:</span>
                <span className="text-zinc-200">{sector}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">CR12 / Incorporation Reg No:</span>
                <span className="text-zinc-200 font-mono">{industry.registrationNumber || industry.regNo || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">KRA PIN Number:</span>
                <span className="text-zinc-200 font-mono">{industry.kraPinNumber || industry.kraPin || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Headquarters Address:</span>
                <span className="text-zinc-200">{industry.headquartersAddress || industry.address || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">County / Location:</span>
                <span className="text-zinc-200">{industry.county || 'Nairobi'}, {industry.country || 'Kenya'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Company Size / Headcount:</span>
                <span className="text-zinc-200">{industry.employeeCountBand || industry.employeeCount || '51-200 Employees'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Official Website:</span>
                <span className="text-teal-300 font-mono">{industry.officialWebsite || industry.website || '-'}</span>
              </div>
            </div>
          </div>

          {/* Section 2: Technical Stacks & Talent Requirements */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5 space-y-3">
            <h3 className="text-xs font-black uppercase tracking-widest text-teal-400 border-b border-zinc-800 pb-2 flex items-center gap-2">
              <Laptop size={14} /> Technical Stacks &amp; Placement Criteria
            </h3>
            <div className="space-y-2 text-xs">
              <div>
                <span className="text-zinc-500 font-bold block mb-1">Primary Tech Stacks:</span>
                <div className="flex flex-wrap gap-1.5">
                  {(industry.primaryTechStacks || 'React, Node.js, Python, PostgreSQL').split(',').map((stack, i) => (
                    <span key={i} className="px-2 py-0.5 rounded bg-zinc-800 text-teal-300 font-mono text-[11px] border border-zinc-700">
                      {stack.trim()}
                    </span>
                  ))}
                </div>
              </div>
              {industry.secondaryTechStacks && (
                <div>
                  <span className="text-zinc-500 font-bold block mb-1">Secondary / DevOps Stacks:</span>
                  <div className="flex flex-wrap gap-1.5">
                    {industry.secondaryTechStacks.split(',').map((stack, i) => (
                      <span key={i} className="px-2 py-0.5 rounded bg-zinc-800 text-zinc-300 font-mono text-[11px] border border-zinc-700">
                        {stack.trim()}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 pt-2">
                <div>
                  <span className="text-zinc-500 font-bold block">Mentorship Hours / Wk:</span>
                  <span className="text-zinc-200 font-mono font-bold">{industry.mentoringHoursWeekly || 4} Hours</span>
                </div>
                <div>
                  <span className="text-zinc-500 font-bold block">CSR / Scholarship Budget:</span>
                  <span className="text-zinc-200 font-mono font-bold">KES {(industry.csrScholarshipBudget || 0).toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Section 3: Contact Credentials */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5 space-y-3">
            <h3 className="text-xs font-black uppercase tracking-widest text-teal-400 border-b border-zinc-800 pb-2 flex items-center gap-2">
              <User size={14} /> Talent &amp; Corporate Contacts
            </h3>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="text-zinc-500 font-bold block">Primary Contact Person:</span>
                <span className="text-zinc-200 font-bold">{industry.primaryContactName || industry.contactPerson || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Job Title / Designation:</span>
                <span className="text-zinc-200">{industry.primaryContactJobTitle || 'Head of Talent'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Official Email:</span>
                <span className="text-zinc-200 font-mono">{industry.primaryContactEmail || industry.email || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Direct Phone / WhatsApp:</span>
                <span className="text-zinc-200 font-mono">{industry.primaryContactPhone || industry.phone || '-'}</span>
              </div>
              {industry.secondaryContactName && (
                <>
                  <div>
                    <span className="text-zinc-500 font-bold block">Secondary HR Lead:</span>
                    <span className="text-zinc-200">{industry.secondaryContactName}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500 font-bold block">Secondary Email:</span>
                    <span className="text-zinc-200 font-mono">{industry.secondaryContactEmail || '-'}</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Section 4: Billing & Compliance */}
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-5 space-y-3">
            <h3 className="text-xs font-black uppercase tracking-widest text-teal-400 border-b border-zinc-800 pb-2 flex items-center gap-2">
              <DollarSign size={14} /> Billing &amp; Compliance Dockets
            </h3>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="text-zinc-500 font-bold block">Partnership Tier:</span>
                <span className="text-amber-400 font-bold uppercase">{tier} Tier</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Billing Commitment:</span>
                <span className="text-zinc-200 font-mono font-bold">KES {(industry.paymentAmount || 25000).toLocaleString()} / mo</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Payment Ref / Code:</span>
                <span className="text-amber-400 font-mono font-bold">{industry.paymentReference || industry.mpesaTransactionCode || '-'}</span>
              </div>
              <div>
                <span className="text-zinc-500 font-bold block">Billing Cycle:</span>
                <span className="text-zinc-200">{industry.billingCycle || 'Monthly Subscription'}</span>
              </div>
            </div>

            {/* Compliance Documents */}
            {(industry.certificateOfIncorporationUrl || industry.taxComplianceCertUrl || industry.cr12Url) && (
              <div className="pt-3 border-t border-zinc-800/60 space-y-2">
                <span className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block">Verification Documents</span>
                <div className="flex flex-wrap gap-2">
                  {industry.certificateOfIncorporationUrl && (
                    <a href={industry.certificateOfIncorporationUrl} target="_blank" rel="noreferrer" className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-xs text-white rounded-lg flex items-center gap-1 font-bold">
                      <ExternalLink size={11} /> Cert of Incorporation
                    </a>
                  )}
                  {industry.taxComplianceCertUrl && (
                    <a href={industry.taxComplianceCertUrl} target="_blank" rel="noreferrer" className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-xs text-white rounded-lg flex items-center gap-1 font-bold">
                      <ExternalLink size={11} /> Tax Compliance Cert
                    </a>
                  )}
                  {industry.cr12Url && (
                    <a href={industry.cr12Url} target="_blank" rel="noreferrer" className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-xs text-white rounded-lg flex items-center gap-1 font-bold">
                      <ExternalLink size={11} /> CR12 Official Extract
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Admin Status Switcher */}
          <div className="p-4 bg-zinc-900 border border-zinc-800 rounded-2xl flex items-center justify-between">
            <span className="text-xs font-black uppercase tracking-wider text-zinc-400">Employer Status</span>
            <div className="flex gap-2">
              <button 
                onClick={() => onUpdateStatus(industry.id, 'Active')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors cursor-pointer ${industry.status === 'Active' ? 'bg-emerald-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}
              >
                Active
              </button>
              <button 
                onClick={() => onUpdateStatus(industry.id, 'Pending Review')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors cursor-pointer ${industry.status === 'Pending Review' ? 'bg-amber-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}
              >
                Under Review
              </button>
              <button 
                onClick={() => onUpdateStatus(industry.id, 'Inactive')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors cursor-pointer ${industry.status === 'Inactive' ? 'bg-red-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:text-white'}`}
              >
                Inactive
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const IndustryManager = ({ showDemo, setShowDemo }) => {
    const navigate = useNavigate();
    const [industries, setIndustries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusMessage, setStatusMessage] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [selectedIndustry, setSelectedIndustry] = useState(null);
    const [pinnedColumn, setPinnedColumn] = useState('companyLegalName');

    // Comprehensive Registration Form State (Matching 100% of onboarding-industry.html)
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [name, setName] = useState('');
    const [registrationNumber, setRegistrationNumber] = useState('');
    const [kraPinNumber, setKraPinNumber] = useState('');
    const [officialWebsite, setOfficialWebsite] = useState('');
    const [headquartersAddress, setHeadquartersAddress] = useState('');
    const [county, setCounty] = useState('Nairobi');
    const [country, setCountry] = useState('Kenya');
    const [postalCode, setPostalCode] = useState('00100');

    const [industrySector, setIndustrySector] = useState('Fintech');
    const [employeeCountBand, setEmployeeCountBand] = useState('51-200 Employees');
    const [yearEstablished, setYearEstablished] = useState('2018');
    const [primaryTechStacks, setPrimaryTechStacks] = useState('React, Node.js, Python, PostgreSQL');
    const [secondaryTechStacks, setSecondaryTechStacks] = useState('AWS, Docker, Kubernetes, Redis');

    const [partnershipTier, setPartnershipTier] = useState('Growth');
    const [hiringTarget, setHiringTarget] = useState(15);
    const [internshipSlots, setInternshipSlots] = useState(5);
    const [mentoringHours, setMentoringHours] = useState(4);
    const [csrBudget, setCsrBudget] = useState(250000);

    const [primaryContactName, setPrimaryContactName] = useState('');
    const [primaryContactJobTitle, setPrimaryContactJobTitle] = useState('Head of Engineering');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [linkedInUrl, setLinkedInUrl] = useState('');
    const [secondaryContactName, setSecondaryContactName] = useState('');
    const [secondaryContactEmail, setSecondaryContactEmail] = useState('');
    const [secondaryContactPhone, setSecondaryContactPhone] = useState('');

    const [certIncorporationUrl, setCertIncorporationUrl] = useState('');
    const [taxComplianceCertUrl, setTaxComplianceCertUrl] = useState('');
    const [cr12Url, setCr12Url] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('M-Pesa Paybill');
    const [paymentReference, setPaymentReference] = useState('');
    const [billingCycle, setBillingCycle] = useState('Monthly Subscription');

    const fetchIndustries = async () => {
        setLoading(true);
        try {
            const snap = await getDocs(collection(db, 'industries'));
            const data = [];
            snap.forEach(d => data.push({ id: d.id, ...d.data() }));

            if (showDemo) {
                data.unshift(
                  {
                    id: 'safaricom-alpha',
                    companyLegalName: 'Safaricom PLC',
                    name: 'Safaricom PLC',
                    registrationNumber: 'CPR/2002/10928',
                    kraPinNumber: 'P051122334A',
                    officialWebsite: 'https://safaricom.co.ke',
                    headquartersAddress: 'Safaricom House, HQ2, Waiyaki Way',
                    county: 'Nairobi',
                    country: 'Kenya',
                    postalCode: '00100',
                    industrySector: 'Telecommunications',
                    employeeCountBand: '1000+ Employees',
                    yearEstablished: '1997',
                    primaryTechStacks: 'Java, Spring Boot, React, AWS, Kubernetes, Kafka',
                    secondaryTechStacks: 'Golang, Python, Docker, Spark',
                    partnershipTier: 'Enterprise',
                    annualTechHiringTarget: 40,
                    hiringTarget: 40,
                    internshipSlotsPerCohort: 15,
                    mentoringHoursWeekly: 10,
                    csrScholarshipBudget: 1500000,
                    primaryContactName: 'Dr. Michael Joseph',
                    primaryContactJobTitle: 'Director of Talent & Tech Academies',
                    email: 'mjoseph@safaricom.co.ke',
                    phone: '+254 722 000 000',
                    paymentAmount: 50000,
                    paymentReference: 'MPESA-SAF-ENG9912',
                    status: 'Active',
                    addedAt: '2026-07-20T10:00:00Z'
                  },
                  {
                    id: 'cellulant-corp',
                    companyLegalName: 'Cellulant Payments Africa',
                    name: 'Cellulant Payments Africa',
                    registrationNumber: 'CPR/2004/00881',
                    kraPinNumber: 'P051992211B',
                    officialWebsite: 'https://cellulant.io',
                    headquartersAddress: 'The Mirage, Tower 2, Westlands',
                    county: 'Nairobi',
                    country: 'Kenya',
                    postalCode: '00800',
                    industrySector: 'Fintech',
                    employeeCountBand: '201-1000 Employees',
                    yearEstablished: '2004',
                    primaryTechStacks: 'Node.js, PostgreSQL, React, AWS, Docker',
                    secondaryTechStacks: 'Python, Redis, Microservices',
                    partnershipTier: 'Growth',
                    annualTechHiringTarget: 18,
                    hiringTarget: 18,
                    internshipSlotsPerCohort: 6,
                    mentoringHoursWeekly: 6,
                    csrScholarshipBudget: 500000,
                    primaryContactName: 'Grace Muthoni',
                    primaryContactJobTitle: 'VP of Engineering Talent',
                    email: 'talent@cellulant.io',
                    phone: '+254 712 888 999',
                    paymentAmount: 25000,
                    paymentReference: 'WIRE-CEL-2026-01',
                    status: 'Active',
                    addedAt: '2026-08-10T14:15:00Z'
                  }
                );
            }
            setIndustries(data);
        } catch (e) {
            console.error("Error fetching industries:", e);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchIndustries();
    }, [showDemo]);

    const handleAdd = async (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        setStatusMessage('saving');

        const industryId = name.trim().toLowerCase().replace(/[^a-z0-9]/g, '-');
        const fee = partnershipTier === 'Enterprise' ? 50000 : partnershipTier === 'Growth' ? 25000 : 15000;

        try {
            await setDoc(doc(db, 'industries', industryId), {
                companyLegalName: name.trim(),
                name: name.trim(),
                registrationNumber: registrationNumber.trim(),
                kraPinNumber: kraPinNumber.trim(),
                officialWebsite: officialWebsite.trim(),
                headquartersAddress: headquartersAddress.trim(),
                county: county.trim(),
                country: country.trim(),
                postalCode: postalCode.trim(),
                industrySector,
                employeeCountBand,
                yearEstablished: yearEstablished.trim(),
                primaryTechStacks: primaryTechStacks.trim(),
                secondaryTechStacks: secondaryTechStacks.trim(),
                partnershipTier,
                annualTechHiringTarget: parseInt(hiringTarget) || 0,
                hiringTarget: parseInt(hiringTarget) || 0,
                internshipSlotsPerCohort: parseInt(internshipSlots) || 0,
                mentoringHoursWeekly: parseInt(mentoringHours) || 0,
                csrScholarshipBudget: parseInt(csrBudget) || 0,
                primaryContactName: primaryContactName.trim(),
                primaryContactJobTitle: primaryContactJobTitle.trim(),
                primaryContactEmail: email.trim().toLowerCase(),
                email: email.trim().toLowerCase(),
                primaryContactPhone: phone.trim(),
                phone: phone.trim(),
                linkedInUrl: linkedInUrl.trim(),
                secondaryContactName: secondaryContactName.trim(),
                secondaryContactEmail: secondaryContactEmail.trim().toLowerCase(),
                secondaryContactPhone: secondaryContactPhone.trim(),
                certificateOfIncorporationUrl: certIncorporationUrl.trim(),
                taxComplianceCertUrl: taxComplianceCertUrl.trim(),
                cr12Url: cr12Url.trim(),
                paymentMethod,
                paymentReference: paymentReference.trim(),
                paymentAmount: fee,
                billingCycle,
                status: 'Active',
                updatedAt: new Date().toISOString(),
                createdAt: new Date().toISOString()
            }, { merge: true });

            setName('');
            setRegistrationNumber('');
            setKraPinNumber('');
            setOfficialWebsite('');
            setHeadquartersAddress('');
            setPrimaryContactName('');
            setEmail('');
            setPhone('');
            setPaymentReference('');
            setIsFormOpen(false);
            
            setStatusMessage('success');
            fetchIndustries();
            setTimeout(() => setStatusMessage(''), 3000);
        } catch (e) {
            console.error("Error adding industry partner:", e);
            setStatusMessage('error');
        }
    };

    const handleUpdateStatus = async (id, newStatus) => {
      try {
        await setDoc(doc(db, 'industries', id), {
          status: newStatus,
          statusUpdatedAt: new Date().toISOString()
        }, { merge: true });
        
        setIndustries(prev => prev.map(i => i.id === id ? { ...i, status: newStatus } : i));
        if (selectedIndustry && selectedIndustry.id === id) {
          setSelectedIndustry(prev => ({ ...prev, status: newStatus }));
        }
      } catch (err) {
        console.error("Failed to update industry status:", err);
      }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Permanently remove this industry partner from the registry?")) return;
        try {
            await deleteDoc(doc(db, 'industries', id));
            if (selectedIndustry?.id === id) setSelectedIndustry(null);
            fetchIndustries();
        } catch (e) {
            console.error("Error deleting:", e);
        }
    };

    const filteredIndustries = industries.filter(i => {
      const matchesSector = filterType === 'all' || (i.industrySector || i.sector) === filterType;
      const query = searchTerm.toLowerCase();
      const compName = (i.companyLegalName || i.name || '').toLowerCase();
      const contact = (i.primaryContactName || i.email || '').toLowerCase();
      const loc = (i.county || i.headquartersCountry || '').toLowerCase();
      const reg = (i.registrationNumber || i.kraPinNumber || '').toLowerCase();
      const matchesSearch = !searchTerm || compName.includes(query) || contact.includes(query) || loc.includes(query) || reg.includes(query);
      return matchesSector && matchesSearch;
    });

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
        setSortConfig({ key, direction });
    };

    const sortedIndustries = [...filteredIndustries].sort((a, b) => {
        if (!sortConfig.key) return 0;
        let aVal = (a[sortConfig.key] || '').toString().toLowerCase();
        let bVal = (b[sortConfig.key] || '').toString().toLowerCase();
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
    });

    return (
        <div className="space-y-8 animate-in fade-in duration-300">
            {/* Header Banner */}
            <div className="bg-gradient-to-r from-zinc-950 via-zinc-900 to-black border border-zinc-800 rounded-3xl p-8 shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-teal-500/10 blur-3xl rounded-full pointer-events-none -mr-20 -mt-20" />
                <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <span className="p-2 bg-teal-500/20 text-teal-400 border border-teal-500/30 rounded-xl">
                                <Factory size={18} />
                            </span>
                            <span className="px-3 py-1 bg-amber-500/10 text-amber-400 border border-amber-500/20 text-[10px] font-black uppercase tracking-widest rounded-full">
                                Portal 03 &bull; Enterprise Talent Pipeline
                            </span>
                        </div>
                        <h1 className="text-3xl font-black text-white uppercase tracking-tight">Industry Partners &amp; Employers</h1>
                        <p className="text-xs font-bold text-zinc-400 uppercase tracking-widest mt-2 max-w-xl">
                            Corporate hiring partners, attachment providers, internship pipelines, and talent placement agreements across Africa.
                        </p>
                    </div>

                    <div className="flex items-center gap-3">
                        <button 
                            onClick={() => setIsFormOpen(!isFormOpen)}
                            className="bg-teal-600 text-white font-black uppercase tracking-widest text-xs px-6 py-3.5 rounded-xl hover:bg-teal-700 transition-all shadow-lg flex items-center gap-2 cursor-pointer"
                        >
                            <Building size={16} /> {isFormOpen ? 'Close Form' : 'Register Corporate Employer'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Comprehensive Creation / Registration Form (Matching onboarding-industry.html 100%) */}
            {isFormOpen && (
              <div className="bg-zinc-950 border border-zinc-800 p-8 rounded-3xl shadow-2xl space-y-6">
                  <div className="flex items-center justify-between border-b border-zinc-800 pb-4">
                      <div>
                          <h2 className="text-base font-black uppercase tracking-widest text-teal-400 flex items-center gap-2">
                              <Building size={18} /> Register Corporate Partner &bull; Zero Data Loss Schema
                          </h2>
                          <p className="text-xs text-zinc-400 mt-1">
                              Record legal incorporation, KRA PIN, technical stack requirements, annual hiring quota, and talent leads.
                          </p>
                      </div>
                      <button 
                          onClick={() => setIsFormOpen(false)}
                          className="p-2 text-zinc-400 hover:text-white bg-zinc-900 border border-zinc-800 rounded-xl transition-colors cursor-pointer"
                      >
                          <X size={18} />
                      </button>
                  </div>

                  <form onSubmit={handleAdd} className="space-y-6">
                      {/* Section 1: Entity Identity */}
                      <div className="p-5 bg-zinc-900/60 rounded-2xl border border-zinc-800/80 space-y-4">
                          <h3 className="text-xs font-black uppercase tracking-widest text-teal-400 flex items-center gap-2">
                              <Building size={14} /> 1. Corporate Identity &amp; Physical Headquarters
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                              <div className="md:col-span-2">
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Company Legal Name *</label>
                                  <input 
                                      type="text" 
                                      value={name} 
                                      onChange={e => setName(e.target.value)} 
                                      placeholder="e.g. Safaricom PLC / Cellulant Africa" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-teal-500"
                                      required
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">CR12 / Incorporation Reg No *</label>
                                  <input 
                                      type="text" 
                                      value={registrationNumber} 
                                      onChange={e => setRegistrationNumber(e.target.value)} 
                                      placeholder="e.g. CPR/2002/10928" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-teal-500"
                                      required
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">KRA PIN Number *</label>
                                  <input 
                                      type="text" 
                                      value={kraPinNumber} 
                                      onChange={e => setKraPinNumber(e.target.value)} 
                                      placeholder="e.g. P051122334A" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-teal-500"
                                      required
                                  />
                              </div>
                              <div className="md:col-span-2">
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Physical HQ Street Address *</label>
                                  <input 
                                      type="text" 
                                      value={headquartersAddress} 
                                      onChange={e => setHeadquartersAddress(e.target.value)} 
                                      placeholder="e.g. Waiyaki Way, HQ Block B" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-teal-500"
                                      required
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">County / City *</label>
                                  <input 
                                      type="text" 
                                      value={county} 
                                      onChange={e => setCounty(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-teal-500"
                                      required
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Official Website URL</label>
                                  <input 
                                      type="url" 
                                      value={officialWebsite} 
                                      onChange={e => setOfficialWebsite(e.target.value)} 
                                      placeholder="https://company.com" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-teal-500"
                                  />
                              </div>
                          </div>
                      </div>

                      {/* Section 2: Industry Sector & Operations */}
                      <div className="p-5 bg-zinc-900/60 rounded-2xl border border-zinc-800/80 space-y-4">
                          <h3 className="text-xs font-black uppercase tracking-widest text-teal-400 flex items-center gap-2">
                              <Factory size={14} /> 2. Sector, Headcount &amp; Technical Stacks
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Industry Sector</label>
                                  <select 
                                      value={industrySector} 
                                      onChange={e => setIndustrySector(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-teal-500 font-bold"
                                  >
                                      <option>Fintech</option>
                                      <option>AgTech</option>
                                      <option>HealthTech</option>
                                      <option>Software Agency</option>
                                      <option>E-Commerce</option>
                                      <option>Telecommunications</option>
                                      <option>Government / Parastatal</option>
                                      <option>Manufacturing & Industrial</option>
                                  </select>
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Employee Headcount Band</label>
                                  <select 
                                      value={employeeCountBand} 
                                      onChange={e => setEmployeeCountBand(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-teal-500 font-bold"
                                  >
                                      <option>1-10 Employees</option>
                                      <option>11-50 Employees</option>
                                      <option>51-200 Employees</option>
                                      <option>201-1000 Employees</option>
                                      <option>1000+ Employees</option>
                                  </select>
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Year Established</label>
                                  <input 
                                      type="text" 
                                      value={yearEstablished} 
                                      onChange={e => setYearEstablished(e.target.value)} 
                                      placeholder="e.g. 2018" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-teal-500"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Partnership Tier</label>
                                  <select 
                                      value={partnershipTier} 
                                      onChange={e => setPartnershipTier(e.target.value)} 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-teal-500 font-bold"
                                  >
                                      <option value="Starter">Starter (KES 15,000 / mo)</option>
                                      <option value="Growth">Growth (KES 25,000 / mo)</option>
                                      <option value="Enterprise">Enterprise (KES 50,000 / mo)</option>
                                  </select>
                              </div>
                              <div className="md:col-span-2">
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Primary Tech Stacks (comma separated) *</label>
                                  <input 
                                      type="text" 
                                      value={primaryTechStacks} 
                                      onChange={e => setPrimaryTechStacks(e.target.value)} 
                                      placeholder="e.g. React, Node.js, Python, PostgreSQL" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-teal-500"
                                      required
                                  />
                              </div>
                              <div className="md:col-span-2">
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Secondary / DevOps Stacks</label>
                                  <input 
                                      type="text" 
                                      value={secondaryTechStacks} 
                                      onChange={e => setSecondaryTechStacks(e.target.value)} 
                                      placeholder="e.g. AWS, Docker, Kubernetes, Redis" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-teal-500"
                                  />
                              </div>
                          </div>
                      </div>

                      {/* Section 3: Commitments & Quotas */}
                      <div className="p-5 bg-zinc-900/60 rounded-2xl border border-zinc-800/80 space-y-4">
                          <h3 className="text-xs font-black uppercase tracking-widest text-teal-400 flex items-center gap-2">
                              <Award size={14} /> 3. Talent Quotas &amp; Mentoring Commitments
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Annual Hiring Target</label>
                                  <input 
                                      type="number" 
                                      value={hiringTarget} 
                                      onChange={e => setHiringTarget(e.target.value)} 
                                      placeholder="15" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white text-xs focus:outline-none focus:border-teal-500"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Internship Slots / Cohort</label>
                                  <input 
                                      type="number" 
                                      value={internshipSlots} 
                                      onChange={e => setInternshipSlots(e.target.value)} 
                                      placeholder="5" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white text-xs focus:outline-none focus:border-teal-500"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Weekly Mentoring Hours</label>
                                  <input 
                                      type="number" 
                                      value={mentoringHours} 
                                      onChange={e => setMentoringHours(e.target.value)} 
                                      placeholder="4" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white text-xs focus:outline-none focus:border-teal-500"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">CSR / Scholarship Budget (KES)</label>
                                  <input 
                                      type="number" 
                                      value={csrBudget} 
                                      onChange={e => setCsrBudget(e.target.value)} 
                                      placeholder="250000" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white text-xs focus:outline-none focus:border-teal-500"
                                  />
                              </div>
                          </div>
                      </div>

                      {/* Section 4: Primary & Secondary Contacts */}
                      <div className="p-5 bg-zinc-900/60 rounded-2xl border border-zinc-800/80 space-y-4">
                          <h3 className="text-xs font-black uppercase tracking-widest text-teal-400 flex items-center gap-2">
                              <User size={14} /> 4. Talent Leads &amp; HR Representatives
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Primary Talent Lead Name *</label>
                                  <input 
                                      type="text" 
                                      value={primaryContactName} 
                                      onChange={e => setPrimaryContactName(e.target.value)} 
                                      placeholder="e.g. Grace Muthoni" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-teal-500"
                                      required
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Job Title *</label>
                                  <input 
                                      type="text" 
                                      value={primaryContactJobTitle} 
                                      onChange={e => setPrimaryContactJobTitle(e.target.value)} 
                                      placeholder="e.g. VP of Engineering" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-teal-500"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Official Talent Email *</label>
                                  <input 
                                      type="email" 
                                      value={email} 
                                      onChange={e => setEmail(e.target.value)} 
                                      placeholder="talent@company.com" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-teal-500"
                                      required
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Direct Phone / WhatsApp *</label>
                                  <input 
                                      type="tel" 
                                      value={phone} 
                                      onChange={e => setPhone(e.target.value)} 
                                      placeholder="+254 7XX XXX XXX" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs font-mono focus:outline-none focus:border-teal-500"
                                      required
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">LinkedIn Profile URL</label>
                                  <input 
                                      type="url" 
                                      value={linkedInUrl} 
                                      onChange={e => setLinkedInUrl(e.target.value)} 
                                      placeholder="https://linkedin.com/in/..." 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-white text-xs focus:outline-none focus:border-teal-500"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">M-Pesa / Wire Payment Ref</label>
                                  <input 
                                      type="text" 
                                      value={paymentReference} 
                                      onChange={e => setPaymentReference(e.target.value)} 
                                      placeholder="e.g. QWE8910XYZ" 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-amber-400 font-mono font-bold text-xs focus:outline-none focus:border-teal-500"
                                  />
                              </div>
                          </div>
                      </div>

                      {/* Section 5: Verification Documents */}
                      <div className="p-5 bg-zinc-900/60 rounded-2xl border border-zinc-800/80 space-y-4">
                          <h3 className="text-xs font-black uppercase tracking-widest text-teal-400 flex items-center gap-2">
                              <FileText size={14} /> 5. Verification Documents &amp; Legal Proof
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Certificate of Incorporation URL</label>
                                  <input 
                                      type="url" 
                                      value={certIncorporationUrl} 
                                      onChange={e => setCertIncorporationUrl(e.target.value)} 
                                      placeholder="https://..." 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white text-xs focus:outline-none focus:border-teal-500"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">Tax Compliance Certificate URL</label>
                                  <input 
                                      type="url" 
                                      value={taxComplianceCertUrl} 
                                      onChange={e => setTaxComplianceCertUrl(e.target.value)} 
                                      placeholder="https://..." 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white text-xs focus:outline-none focus:border-teal-500"
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] font-black uppercase tracking-wider text-zinc-400 block mb-1">CR12 Official Extract URL</label>
                                  <input 
                                      type="url" 
                                      value={cr12Url} 
                                      onChange={e => setCr12Url(e.target.value)} 
                                      placeholder="https://..." 
                                      className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-white text-xs focus:outline-none focus:border-teal-500"
                                  />
                              </div>
                          </div>
                      </div>

                      <div className="flex justify-end gap-3 pt-2">
                          <button 
                              type="button" 
                              onClick={() => setIsFormOpen(false)}
                              className="px-6 py-3 rounded-xl border border-zinc-800 text-zinc-400 hover:text-white text-xs font-black uppercase tracking-widest cursor-pointer"
                          >
                              Cancel
                          </button>
                          <button 
                              type="submit" 
                              disabled={statusMessage === 'saving'}
                              className="bg-teal-600 text-white font-black uppercase tracking-widest text-xs px-8 py-3 rounded-xl hover:bg-teal-700 transition-all shadow-lg cursor-pointer"
                          >
                              {statusMessage === 'saving' ? 'Saving Employer...' : 'Register Corporate Partner'}
                          </button>
                      </div>
                  </form>
                  {statusMessage === 'success' && (
                      <p className="text-emerald-400 text-xs font-bold flex items-center gap-1.5 justify-end">
                          <CheckCircle size={14} /> Employer profile synchronized with Firestore.
                      </p>
                  )}
              </div>
            )}

            {/* Google Sheets-Grade Modular Registry Sheet with Freeze Panes */}
            <div className="bg-zinc-950 border border-zinc-800 rounded-2xl overflow-hidden shadow-2xl">
                {/* Spreadsheet Toolbar */}
                <div className="p-4 border-b border-zinc-800 flex flex-wrap gap-4 justify-between items-center bg-zinc-900/60">
                    <div className="flex flex-1 items-center gap-3 min-w-[280px]">
                      <Search size={16} className="text-zinc-400" />
                      <input 
                        type="text"
                        placeholder="Search by company, sector, location, contact, or CR12..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="w-full bg-transparent text-sm text-white placeholder-zinc-500 focus:outline-none"
                      />
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Filter size={14} className="text-zinc-400" />
                        <select
                            value={filterType}
                            onChange={e => setFilterType(e.target.value)}
                            className="bg-zinc-900 border border-zinc-800 text-zinc-300 font-bold text-xs uppercase tracking-widest px-3 py-1.5 rounded-lg focus:outline-none"
                        >
                            <option value="all">All Sectors</option>
                            <option value="Fintech">Fintech</option>
                            <option value="AgTech">AgTech</option>
                            <option value="HealthTech">HealthTech</option>
                            <option value="Software Agency">Software Agency</option>
                            <option value="Telecommunications">Telecommunications</option>
                            <option value="Government / Parastatal">Government</option>
                        </select>
                      </div>

                      <button 
                          onClick={fetchIndustries}
                          className="text-[9px] font-black uppercase tracking-widest px-4 py-2 rounded-full border border-zinc-700 text-zinc-400 bg-zinc-900 hover:text-white transition-all shadow-sm cursor-pointer"
                      >
                          Refresh
                      </button>
                    </div>
                </div>

                {/* Horizontal Scrolling High-Density Table with Freeze Panes */}
                <div className="overflow-x-auto relative">
                  <table className="w-full text-left border-collapse whitespace-nowrap text-xs">
                      <thead>
                          <tr className="bg-zinc-900/90 border-b border-zinc-800 select-none">
                              {/* Freeze Pane: Index */}
                              <th className={`w-12 p-3 text-center text-[10px] font-black uppercase tracking-wider text-zinc-500 border-r border-zinc-800 ${
                                pinnedColumn !== 'none' ? 'sticky left-0 z-30 bg-zinc-900' : ''
                              }`}>
                                #
                              </th>

                              {/* Freeze Pane: Company Name */}
                              <th className={`min-w-[220px] p-0 border-r border-zinc-800 ${
                                pinnedColumn === 'companyLegalName' 
                                  ? 'sticky left-12 z-30 bg-zinc-900 border-r-2 border-r-teal-500 shadow-[4px_0_12px_rgba(0,0,0,0.5)]' 
                                  : ''
                              }`}>
                                <div className="w-full h-full p-3 flex items-center justify-between">
                                  <button onClick={() => handleSort('companyLegalName')} className="flex items-center gap-1 font-black uppercase tracking-widest text-zinc-300 hover:text-white">
                                    <span>Company Legal Name</span> {sortConfig.key === 'companyLegalName' && (sortConfig.direction === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
                                  </button>
                                  <button
                                    onClick={() => setPinnedColumn(pinnedColumn === 'companyLegalName' ? 'none' : 'companyLegalName')}
                                    className={`p-1 rounded cursor-pointer ${pinnedColumn === 'companyLegalName' ? 'text-teal-400' : 'text-zinc-600 hover:text-zinc-300'}`}
                                    title={pinnedColumn === 'companyLegalName' ? 'Unpin column' : 'Pin Company Name'}
                                  >
                                    <Pin size={11} className={pinnedColumn === 'companyLegalName' ? 'fill-teal-400' : ''} />
                                  </button>
                                </div>
                              </th>

                              {/* Sector */}
                              <th className="min-w-[140px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Industry Sector
                              </th>

                              {/* Reg / CR12 */}
                              <th className="min-w-[140px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                CR12 / Reg No
                              </th>

                              {/* KRA PIN */}
                              <th className="min-w-[120px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                KRA PIN
                              </th>

                              {/* HQ Location */}
                              <th className="min-w-[160px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Location (County)
                              </th>

                              {/* Headcount */}
                              <th className="min-w-[130px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Headcount
                              </th>

                              {/* Tier */}
                              <th className="min-w-[120px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Tier &amp; Billing
                              </th>

                              {/* Annual Hiring Target */}
                              <th className="min-w-[120px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Hiring Quota
                              </th>

                              {/* Internships */}
                              <th className="min-w-[120px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Internships
                              </th>

                              {/* Primary Tech Stacks */}
                              <th className="min-w-[220px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Primary Tech Stacks
                              </th>

                              {/* Primary Contact */}
                              <th className="min-w-[180px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Talent Lead
                              </th>

                              {/* Contact Phone & Email */}
                              <th className="min-w-[180px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Email &amp; Phone
                              </th>

                              {/* Payment Ref */}
                              <th className="min-w-[130px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Payment Ref
                              </th>

                              {/* Status */}
                              <th className="min-w-[120px] p-3 border-r border-zinc-800 font-black uppercase tracking-widest text-zinc-400">
                                Status
                              </th>

                              {/* Actions */}
                              <th className="min-w-[110px] p-3 text-center font-black uppercase tracking-widest text-zinc-400">
                                Action
                              </th>
                          </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-800/60 font-medium">
                          {loading ? (
                              <tr><td colSpan="16" className="p-8 text-center text-zinc-500 text-xs">Loading Corporate Partners...</td></tr>
                          ) : sortedIndustries.length === 0 ? (
                              <tr><td colSpan="16" className="p-8 text-center text-zinc-500 text-xs">No employers found matching criteria.</td></tr>
                          ) : sortedIndustries.map((industry, idx) => {
                              const companyName = industry.companyLegalName || industry.name || 'Enterprise';
                              const tierVal = industry.partnershipTier || (industry.paymentAmount >= 50000 ? 'Enterprise' : industry.paymentAmount >= 25000 ? 'Growth' : 'Starter');

                              return (
                                <tr 
                                    key={industry.id} 
                                    onClick={() => setSelectedIndustry(industry)}
                                    className="hover:bg-zinc-900/60 transition-colors cursor-pointer group"
                                >
                                    {/* Pinned: Index */}
                                    <td className={`p-3 text-center font-mono text-[11px] text-zinc-500 border-r border-zinc-800 ${
                                      pinnedColumn !== 'none' ? 'sticky left-0 z-20 bg-zinc-950 group-hover:bg-zinc-900' : ''
                                    }`}>
                                        {idx + 1}
                                    </td>

                                    {/* Pinned: Company Name */}
                                    <td className={`p-3 border-r border-zinc-800 ${
                                      pinnedColumn === 'companyLegalName' 
                                        ? 'sticky left-12 z-20 bg-zinc-950 group-hover:bg-zinc-900 border-r-2 border-r-teal-500 shadow-[4px_0_12px_rgba(0,0,0,0.5)]' 
                                        : ''
                                    }`}>
                                        <p className="font-bold text-white group-hover:text-teal-400 transition-colors">{companyName}</p>
                                        <p className="text-[10px] text-zinc-500 font-mono">{industry.officialWebsite || industry.website || '-'}</p>
                                    </td>

                                    {/* Sector */}
                                    <td className="p-3 border-r border-zinc-800 text-zinc-300">
                                        {industry.industrySector || industry.sector || 'Technology'}
                                    </td>

                                    {/* CR12 / Reg No */}
                                    <td className="p-3 border-r border-zinc-800 font-mono text-zinc-400">
                                        {industry.registrationNumber || industry.regNo || '-'}
                                    </td>

                                    {/* KRA PIN */}
                                    <td className="p-3 border-r border-zinc-800 font-mono text-zinc-400">
                                        {industry.kraPinNumber || industry.kraPin || '-'}
                                    </td>

                                    {/* Location */}
                                    <td className="p-3 border-r border-zinc-800 text-zinc-300 font-semibold">
                                        {industry.county || 'Nairobi'}, {industry.country || 'Kenya'}
                                    </td>

                                    {/* Headcount */}
                                    <td className="p-3 border-r border-zinc-800 text-zinc-400">
                                        {industry.employeeCountBand || '51-200'}
                                    </td>

                                    {/* Tier */}
                                    <td className="p-3 border-r border-zinc-800">
                                        <span className="font-bold text-amber-400 uppercase text-[11px] block">{tierVal}</span>
                                        <span className="font-mono text-[10px] text-teal-400">KES {(industry.paymentAmount || 25000).toLocaleString()}</span>
                                    </td>

                                    {/* Hiring Quota */}
                                    <td className="p-3 border-r border-zinc-800 font-mono text-white font-bold">
                                        {industry.annualTechHiringTarget || industry.hiringTarget || 0} Eng
                                    </td>

                                    {/* Internships */}
                                    <td className="p-3 border-r border-zinc-800 font-mono text-teal-300">
                                        {industry.internshipSlotsPerCohort || 0} slots
                                    </td>

                                    {/* Primary Tech Stacks */}
                                    <td className="p-3 border-r border-zinc-800 max-w-[220px] truncate" title={industry.primaryTechStacks}>
                                        <span className="font-mono text-[11px] text-zinc-300">{industry.primaryTechStacks || '-'}</span>
                                    </td>

                                    {/* Talent Lead */}
                                    <td className="p-3 border-r border-zinc-800">
                                        <p className="text-white font-bold">{industry.primaryContactName || industry.contactPerson || '-'}</p>
                                        <p className="text-[10px] text-zinc-500">{industry.primaryContactJobTitle || 'Talent Lead'}</p>
                                    </td>

                                    {/* Email & Phone */}
                                    <td className="p-3 border-r border-zinc-800 font-mono text-[11px]">
                                        <p className="text-zinc-300">{industry.primaryContactEmail || industry.email || '-'}</p>
                                        <p className="text-zinc-500">{industry.primaryContactPhone || industry.phone || '-'}</p>
                                    </td>

                                    {/* Payment Ref */}
                                    <td className="p-3 border-r border-zinc-800 font-mono text-[11px] text-amber-400">
                                        {industry.paymentReference || industry.mpesaTransactionCode || '-'}
                                    </td>

                                    {/* Status */}
                                    <td className="p-3 border-r border-zinc-800">
                                        <span className={`px-2.5 py-1 text-[10px] font-black uppercase tracking-wider rounded-full border ${
                                          industry.status === 'Active' 
                                            ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' 
                                            : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                                        }`}>
                                          {industry.status || 'Active'}
                                        </span>
                                    </td>

                                    {/* Action */}
                                    <td className="p-3 text-center">
                                        <div className="flex items-center justify-center gap-1.5" onClick={e => e.stopPropagation()}>
                                            <button 
                                                onClick={() => setSelectedIndustry(industry)}
                                                className="px-2.5 py-1 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-300 hover:text-white rounded-lg text-xs font-bold transition-colors cursor-pointer"
                                                title="Inspect Corporate Profile"
                                            >
                                                <Eye size={12} />
                                            </button>
                                            <button 
                                                onClick={() => handleDelete(industry.id)}
                                                className="p-1 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors cursor-pointer"
                                                title="Delete Partner"
                                            >
                                                <Trash2 size={13} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                              );
                          })}
                      </tbody>
                  </table>
                </div>
            </div>

            {/* Slide-over Full Dossier Drawer */}
            <IndustryDetailDrawer 
              industry={selectedIndustry} 
              onClose={() => setSelectedIndustry(null)} 
              onUpdateStatus={handleUpdateStatus}
            />
        </div>
    );
};

export default IndustryManager;
